import { useEffect, useState } from "react";
import { ArrowLeft, ArrowRight, Loader2, Sparkles, CheckCircle2 } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";

export default function InterviewWizard({ type, onBack, onCreated }) {
  const [data, setData] = useState(null);
  const [answers, setAnswers] = useState({});
  const [step, setStep] = useState(0);
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    api.get(`/contracts/catalog/${type.id}`).then(({ data }) => {
      setData(data);
      const init = {};
      data.interview.forEach((q) => { if (q.prefill) init[q.key] = q.prefill; });
      setAnswers(init);
    }).catch((e) => toast.error(formatApiError(e.response?.data?.detail) || "Could not load"));
  }, [type]);

  if (!data) return <div className="flex justify-center py-24 text-[#71717A]"><Loader2 className="w-6 h-6 animate-spin" /></div>;

  const questions = data.interview;
  const perStep = 3;
  const steps = Math.max(1, Math.ceil(questions.length / perStep));
  const current = questions.slice(step * perStep, step * perStep + perStep);
  const isLast = step >= steps - 1;
  const set = (k, v) => setAnswers((a) => ({ ...a, [k]: v }));

  const create = async () => {
    setCreating(true);
    try {
      const { data: c } = await api.post("/contracts", { type_id: type.id, answers });
      toast.success("Draft created — now edit and finalize");
      onCreated(c);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Could not create"); }
    finally { setCreating(false); }
  };

  const prefilledKeys = Object.entries(data.autofill).filter(([, v]) => v).map(([k]) => k);

  return (
    <div className="fv-fade-up max-w-2xl" data-testid="interview-wizard">
      <button onClick={onBack} data-testid="interview-back" className="flex items-center gap-1.5 text-sm text-[#A1A1AA] hover:text-white mb-5"><ArrowLeft className="w-4 h-4" /> Back</button>
      <div className="flex items-center gap-2 mb-1">
        <h2 className="font-heading text-2xl text-white">{data.type.name}</h2>
      </div>
      <p className="text-sm text-[#71717A] mb-4">{data.type.desc}</p>

      <div className="rounded-xl border border-[#D4AF37]/25 bg-[#151310] p-3 mb-6 flex gap-2" data-testid="autofill-notice">
        <Sparkles className="w-4 h-4 text-[#D4AF37] shrink-0 mt-0.5" />
        <p className="text-xs text-[#E4E4E7]">Auto-filled from your Business Profile: <span className="text-[#D4AF37]">{data.autofill.business_name}</span>{data.autofill.business_state ? `, ${data.autofill.business_state}` : ""}. You won't be asked for details we already have.</p>
      </div>

      <div className="flex items-center gap-1.5 mb-6">
        {Array.from({ length: steps }).map((_, i) => (
          <div key={i} className={`h-1.5 flex-1 rounded-full ${i <= step ? "bg-[#D4AF37]" : "bg-white/10"}`} />
        ))}
      </div>

      <div className="space-y-4 mb-6" data-testid="interview-fields">
        {current.map((q) => (
          <div key={q.key}>
            <label className="text-sm text-white mb-1.5 block">{q.label} {q.optional && <span className="text-[#52525B] text-xs">(optional)</span>}</label>
            {q.type === "textarea" ? (
              <textarea value={answers[q.key] || ""} onChange={(e) => set(q.key, e.target.value)} rows={3} data-testid={`iq-${q.key}`}
                className="w-full bg-[#0d0d0f] border border-white/10 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-[#D4AF37]/40 resize-none" />
            ) : q.type === "select" ? (
              <Select value={answers[q.key] || ""} onValueChange={(v) => set(q.key, v)}>
                <SelectTrigger className="bg-[#0d0d0f] border-white/10 text-white" data-testid={`iq-${q.key}`}><SelectValue placeholder="Select…" /></SelectTrigger>
                <SelectContent className="bg-[#1C1C1F] border-white/10 text-white">
                  {q.options.map((o) => <SelectItem key={o} value={o} className="focus:bg-white/10">{o}</SelectItem>)}
                </SelectContent>
              </Select>
            ) : (
              <input type={q.type === "date" ? "date" : "text"} value={answers[q.key] || ""} onChange={(e) => set(q.key, e.target.value)} data-testid={`iq-${q.key}`}
                className="w-full bg-[#0d0d0f] border border-white/10 rounded-lg px-3 h-10 text-sm text-white focus:outline-none focus:border-[#D4AF37]/40" />
            )}
            {q.help && <p className="text-[11px] text-[#52525B] mt-1">{q.help}</p>}
          </div>
        ))}
      </div>

      <div className="flex items-center justify-between">
        <Button onClick={() => setStep((s) => Math.max(0, s - 1))} disabled={step === 0} variant="outline"
          data-testid="interview-prev" className="border-white/15 bg-transparent text-white rounded-full disabled:opacity-40">Previous</Button>
        {isLast ? (
          <Button onClick={create} disabled={creating} data-testid="interview-create"
            className="fv-gold-gradient text-[#0A0A0B] font-semibold rounded-full hover:opacity-90">
            {creating ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Generating…</> : <><CheckCircle2 className="w-4 h-4 mr-2" /> Generate draft</>}
          </Button>
        ) : (
          <Button onClick={() => setStep((s) => s + 1)} data-testid="interview-next" className="fv-gold-gradient text-[#0A0A0B] font-semibold rounded-full hover:opacity-90">Next <ArrowRight className="w-4 h-4 ml-1" /></Button>
        )}
      </div>
    </div>
  );
}
